import { motion } from "framer-motion";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

export function Shell({
  children,
  wide = false,
}: {
  children: ReactNode;
  /** Wider column for the design editor stage. */
  wide?: boolean;
}) {
  return (
    <div className="app-bg relative min-h-dvh overflow-x-hidden">
      <div
        className={`relative z-10 mx-auto flex min-h-dvh w-full flex-col px-5 py-8 sm:px-8 sm:py-12 ${
          wide ? "max-w-4xl" : "max-w-2xl sm:max-w-3xl"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export function BrandMark({ compact = false }: { compact?: boolean }) {
  const logoSrc = compact
    ? "/game_logo_sm.png?v=4"
    : "/game_logo.png?v=4";

  return (
    <div
      className={
        compact
          ? "text-left"
          : "mb-6 w-[calc(100%+1.5rem)] max-w-none -translate-x-[0.75rem] text-center sm:mb-8 sm:w-[calc(100%+3rem)] sm:-translate-x-[1.5rem]"
      }
    >
      <img
        src={logoSrc}
        alt="Poppycock"
        className={
          compact
            ? "ml-0 h-11 w-auto max-w-[min(100%,340px)] object-contain object-left sm:h-12"
            : "mx-auto h-auto w-full object-contain"
        }
        decoding="async"
        fetchPriority={compact ? "auto" : "high"}
      />
      {!compact && (
        <p className="mx-auto mt-3 max-w-md px-4 text-sm text-[var(--color-muted)] sm:mt-4 sm:text-base">
          A pure goofy “Who Wrote That?” party game. No synergy. No icebreakers.
          Just absurd creativity.
        </p>
      )}
    </div>
  );
}

export function Card({
  children,
  className = "",
  strong = false,
}: {
  children: ReactNode;
  className?: string;
  strong?: boolean;
}) {
  return (
    <div
      className={`${strong ? "glass-strong" : "glass"} rounded-2xl p-5 sm:p-6 ${className}`}
    >
      {children}
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40";
  const variants: Record<string, string> = {
    primary:
      "bg-[var(--color-accent)] text-[#141414] shadow-[0_8px_28px_rgba(57,181,74,0.28)] hover:bg-[var(--color-accent-soft)]",
    secondary:
      "bg-white/8 text-[var(--color-cream)] border border-white/10 hover:bg-white/12",
    ghost:
      "bg-transparent text-[var(--color-muted)] hover:text-[var(--color-cream)] hover:bg-white/5",
    danger:
      "bg-[rgba(232,93,93,0.15)] text-[var(--color-danger)] border border-[rgba(232,93,93,0.25)] hover:bg-[rgba(232,93,93,0.25)]",
  };

  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Input({
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`w-full rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-[var(--color-cream)] placeholder:text-white/30 outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[rgba(57,181,74,0.28)] ${className}`}
      {...props}
    />
  );
}

export function TextArea({
  className = "",
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={`w-full resize-none rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-[var(--color-cream)] placeholder:text-white/30 outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[rgba(57,181,74,0.28)] ${className}`}
      {...props}
    />
  );
}

export function Label({ children }: { children: ReactNode }) {
  return (
    <label className="mb-1.5 block text-xs font-medium tracking-wide text-[var(--color-muted)] uppercase">
      {children}
    </label>
  );
}

export function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-[rgba(232,93,93,0.3)] bg-[rgba(232,93,93,0.12)] px-4 py-3 text-sm text-[var(--color-danger)]"
    >
      <span>{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-[var(--color-danger)]/80 hover:text-[var(--color-danger)]"
        >
          Dismiss
        </button>
      )}
    </motion.div>
  );
}

export function PhaseBadge({
  phase,
  kind,
}: {
  phase: string;
  /** When phase is writing, distinguish text / design / caption. */
  kind?: "text" | "design" | "caption";
}) {
  const writingLabel =
    kind === "design"
      ? "Designing"
      : kind === "caption"
        ? "Captioning"
        : "Writing";
  const labels: Record<string, string> = {
    lobby: "Lobby",
    writing: writingLabel,
    guessing: "Guessing",
    reveal: "Reveal",
    funniest: "Funniest",
    round_end: "Scores",
    game_over: "Finale",
  };
  return (
    <span className="inline-flex items-center rounded-full border border-[rgba(57,181,74,0.25)] bg-[rgba(57,181,74,0.1)] px-2.5 py-0.5 text-[11px] font-medium tracking-wide text-[var(--color-accent-soft)] uppercase">
      {labels[phase] ?? phase}
    </span>
  );
}

export function ProgressDots({
  submitted,
  total,
}: {
  submitted: number;
  total: number;
}) {
  return (
    <p className="text-sm text-[var(--color-muted)]">
      <span className="font-semibold text-[var(--color-cream)]">{submitted}</span>
      {" / "}
      {total} submitted
    </p>
  );
}
